import { Controller, useFormContext } from "react-hook-form";
import { Table } from "@tricura/atlas-ui";
import { EditableCellProvider, EditableTextCell } from "../../../../components/editable-cell";
import { FormGroup } from "../../../../components/form-group";
import type { ScheduleFormData } from "../schema";

const FIELDS: { key: keyof ScheduleFormData["relativity"]; label: string; tooltip: string }[] = [
    {
        key: "skilled",
        label: "Skilled",
        tooltip:
            "Baseline = 1\n\nEvery occupied SNF bed counts as one full equivalency unit when calculating premium.\n\nClick any cell in the row to override.",
    },
    {
        key: "assisted",
        label: "Assisted",
        tooltip:
            "Baseline = 0.35\n\nEach occupied ALF bed counts as 35% of a skilled bed in the equivalency calculation, reflecting lower care intensity and risk.\n\nClick any cell in the row to override.",
    },
    {
        key: "independent",
        label: "Independent",
        tooltip:
            "Baseline = 0.15\n\nEach ILF bed counts as 15% of a skilled bed in the equivalency calculation, the lowest exposure of the three care levels.\n\nClick any cell in the row to override.",
    },
    {
        key: "additionalExposure",
        label: "Additional Exposure",
        tooltip:
            "Baseline = 1\n\nTreating these beds at full skilled-equivalent risk. Adjust if your underwriting assessment supports a lower factor.\n\nClick any cell in the row to override.",
    },
];

/** Strip everything but digits and a single decimal point. */
const decimal = (raw: string) => {
    const cleaned = raw.replace(/[^\d.]/g, "");
    const firstDot = cleaned.indexOf(".");
    if (firstDot === -1) return cleaned;
    return cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, "");
};

const ADDITIONAL_EXPOSURE_OVER_ONE =
    "Additional exposure beds were added, but the relativity factor is still 1.00 (same as Skilled). Confirm or adjust above.";

export function RelativitySection() {
    const { control } = useFormContext<ScheduleFormData>();

    return (
        <FormGroup title="Relativity" flushBody>
            <EditableCellProvider>
                <Table aria-label="Relativity">
                    <Table.Header>
                        {FIELDS.map(({ key, label, tooltip }, i) => (
                            <Table.Head key={key} id={key} label={label} tooltip={tooltip} isRowHeader={i === 0} className="w-1/4 pl-9" />
                        ))}
                    </Table.Header>
                    <Table.Body items={[{ id: "relativity-row" }]}>
                        {() => (
                            <Table.Row id="relativity-row">
                                {FIELDS.map(({ key, label }) => (
                                    <Table.Cell key={key} className="w-1/4">
                                        <Controller
                                            control={control}
                                            name={`relativity.${key}` as const}
                                            render={({ field, fieldState }) => {
                                                // Soft warning when Additional Exposure exceeds 1.
                                                // The factor stays at 1.00 in calc engines, so going
                                                // over 1 in the UI is almost always a typo.
                                                const overOne = key === "additionalExposure" && !!field.value && Number(field.value) > 1;
                                                const isInvalid = fieldState.invalid || overOne;
                                                const errorMessage =
                                                    fieldState.error?.message ?? (overOne ? ADDITIONAL_EXPOSURE_OVER_ONE : undefined);
                                                return (
                                                    <EditableTextCell
                                                        ariaLabel={label}
                                                        placeholder="0.00"
                                                        value={field.value}
                                                        onChange={field.onChange}
                                                        isInvalid={isInvalid}
                                                        errorMessage={errorMessage}
                                                        inputMode="decimal"
                                                        sanitize={decimal}
                                                        data-autotest={`schedule-relativity-${key}-input`}
                                                    />
                                                );
                                            }}
                                        />
                                    </Table.Cell>
                                ))}
                            </Table.Row>
                        )}
                    </Table.Body>
                </Table>
            </EditableCellProvider>
        </FormGroup>
    );
}
